import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'gold' | 'danger' | 'ghost';
type Size = 'sm' | 'md';

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  // `active:translate-y-px` makes the button visibly "press in" on click —
  // combined with the darker active background it gives the same tactile cue
  // a physical button does. `transition-all duration-100` keeps it snappy.
  const base =
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-100 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0';
  const sizes: Record<Size, string> = {
    sm: 'px-2.5 py-1 text-xs',
    md: 'px-3.5 py-2 text-sm',
  };
  const variants: Record<Variant, string> = {
    primary:
      'bg-brand-navy-700 text-white hover:bg-brand-navy-800 active:bg-brand-navy-900 active:shadow-inner shadow-sm focus-visible:ring-brand-navy-400',
    secondary:
      'bg-white text-brand-navy-700 border border-slate-300 hover:border-brand-navy-300 hover:bg-brand-navy-50 active:bg-brand-navy-100 active:border-brand-navy-400 active:shadow-inner shadow-sm focus-visible:ring-brand-navy-300',
    gold:
      'bg-brand-gold-500 text-white hover:bg-brand-gold-600 active:bg-brand-gold-700 active:shadow-inner shadow-sm focus-visible:ring-brand-gold-400',
    danger:
      'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 active:shadow-inner shadow-sm focus-visible:ring-rose-400',
    ghost: 'text-slate-600 hover:bg-slate-100 active:bg-slate-200 hover:text-slate-900',
  };
  return <button className={clsx(base, sizes[size], variants[variant], className)} {...props} />;
}
